import React, { useEffect, useState } from "react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Sparkles, TrendingUp, Package, AlertTriangle, IndianRupee, Send } from "lucide-react";
import { api } from "../lib/api";
import { useShop } from "../contexts/ShopContext";
import { useAuth } from "../contexts/AuthContext";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, BarChart, Bar, Legend } from "recharts";

const KpiCard = ({ icon: Icon, label, value, sub, testid, accent }) => (
  <Card className="border-border shadow-none rounded-md" data-testid={testid}>
    <CardContent className="p-5">
      <div className="flex items-center gap-3 mb-2">
        <div className={`w-9 h-9 rounded ${accent || "bg-terracotta/10"} flex items-center justify-center`}>
          <Icon size={18} className="text-terracotta" />
        </div>
        <div className="label-overline text-muted-foreground">{label}</div>
      </div>
      <div className="font-heading text-3xl font-bold text-ink">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </CardContent>
  </Card>
);

export default function Dashboard() {
  const { activeShopId, activeShop } = useShop();
  const { user } = useAuth();
  const [session, setSession] = useState(null);
  const [alerts, setAlerts] = useState({ low_stock: [], expiring: [] });
  const [monitor, setMonitor] = useState([]);
  const [aiQuestion, setAiQuestion] = useState("Which products are selling well? Which should I discount?");
  const [aiInsight, setAiInsight] = useState("");
  const [aiLoading, setAiLoading] = useState(false);

  const load = async () => {
    try {
      const [s, a, m] = await Promise.all([
        api.get(`/reports/session?shop_id=${activeShopId}`),
        api.get(`/alerts`),
        api.get(`/reports/sales-monitor?days=30`),
      ]);
      setSession(s.data);
      setAlerts(a.data);
      setMonitor(m.data.data);
    } catch {}
  };

  useEffect(() => { if (activeShopId) load(); }, [activeShopId]);

  const askAI = async () => {
    setAiLoading(true);
    try {
      const r = await api.post("/ai/insights", { question: aiQuestion });
      setAiInsight(r.data.insight);
    } catch {
      setAiInsight("AI temporarily unavailable.");
    } finally {
      setAiLoading(false);
    }
  };

  // chart
  const days = {};
  monitor.forEach((d) => {
    if (!days[d.day]) days[d.day] = { day: d.day.slice(5), shop1: 0, shop2: 0 };
    days[d.day][d.shop_id] = (days[d.day][d.shop_id] || 0) + d.total;
  });
  const chartData = Object.values(days).sort((a, b) => a.day.localeCompare(b.day));

  return (
    <div className="p-8 space-y-6 overflow-y-auto" data-testid="dashboard-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="label-overline text-muted-foreground">Welcome back, {user?.name?.split(" ")[0]}</div>
          <h1 className="font-heading text-4xl font-bold text-ink mt-1">{activeShop?.name || "Dashboard"}</h1>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <KpiCard icon={IndianRupee} label="Today's Sale" value={`₹${(session?.total || 0).toFixed(0)}`} sub={`${session?.bill_count || 0} bills`} testid="kpi-today-sale" />
        <KpiCard icon={TrendingUp} label="Cash" value={`₹${(session?.by_method?.cash || 0).toFixed(0)}`} sub="Today" testid="kpi-cash" accent="bg-moss/10" />
        <KpiCard icon={Sparkles} label="Digital (UPI/Card)" value={`₹${((session?.by_method?.gpay || 0) + (session?.by_method?.mpay || 0) + (session?.by_method?.card || 0)).toFixed(0)}`} sub="GPay/MPay/Card" testid="kpi-digital" accent="bg-ochre/20" />
        <KpiCard icon={AlertTriangle} label="Alerts" value={`${alerts.low_stock.length + alerts.expiring.length}`} sub={`${alerts.low_stock.length} low stock · ${alerts.expiring.length} expiring`} testid="kpi-alerts" accent="bg-destructive/10" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="md:col-span-2 border-border shadow-none">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="font-heading text-lg font-bold">Sales last 30 days</div>
              <div className="text-xs text-muted-foreground">Shop1 vs Shop2</div>
            </div>
            <div style={{ width: "100%", height: 280 }}>
              <ResponsiveContainer>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E0D8" />
                  <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="shop1" fill="#E05236" name="Shop 1" />
                  <Bar dataKey="shop2" fill="#4A7C59" name="Shop 2" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="border-warning/30 shadow-none" style={{ background: "#FFF8E7" }}>
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles size={18} className="text-terracotta" />
              <div className="font-heading text-lg font-bold">AI Insights · GPT-5.2</div>
            </div>
            <Textarea
              data-testid="ai-question"
              value={aiQuestion}
              onChange={(e) => setAiQuestion(e.target.value)}
              className="bg-white border-border min-h-[60px]"
              placeholder="Ask about sales, stock, discounts..."
            />
            <Button onClick={askAI} disabled={aiLoading} data-testid="ai-ask-btn"
              className="mt-2 w-full bg-terracotta hover:bg-terracotta-hover text-white gap-2">
              <Send size={14} /> {aiLoading ? "Thinking…" : "Get insights"}
            </Button>
            {aiInsight && (
              <div className="mt-3 text-sm text-ink whitespace-pre-wrap bg-white p-3 rounded border border-border max-h-72 overflow-y-auto scroll-thin" data-testid="ai-insight-result">
                {aiInsight}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="border-border shadow-none">
          <CardContent className="p-5">
            <div className="font-heading text-lg font-bold mb-2 flex items-center gap-2">
              <Package size={16} className="text-terracotta" /> Low Stock
            </div>
            {alerts.low_stock.length === 0 && <div className="text-sm text-muted-foreground">All good. No reorder needed.</div>}
            <div className="space-y-1">
              {alerts.low_stock.slice(0, 8).map((a, i) => (
                <div key={i} className="flex justify-between text-sm py-1 border-b border-border/60" data-testid="low-stock-row">
                  <span>{a.name} <span className="text-xs text-muted-foreground">({a.shop_id})</span></span>
                  <span className="text-destructive font-medium">{a.qty} left</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="border-border shadow-none">
          <CardContent className="p-5">
            <div className="font-heading text-lg font-bold mb-2 flex items-center gap-2">
              <AlertTriangle size={16} className="text-ochre" /> Expiring Soon
            </div>
            {alerts.expiring.length === 0 && <div className="text-sm text-muted-foreground">No expiry issues.</div>}
            <div className="space-y-1">
              {alerts.expiring.slice(0, 8).map((a, i) => (
                <div key={i} className="flex justify-between text-sm py-1 border-b border-border/60" data-testid="expiry-row">
                  <span>{a.name} <span className="text-xs text-muted-foreground">({a.shop_id})</span></span>
                  <span className={`font-medium ${a.days_left < 0 ? "text-destructive" : "text-ochre"}`}>
                    {a.days_left < 0 ? "EXPIRED" : `${a.days_left}d`}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
